import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChartOutlined as BarChartOutlinedIcon,
  BusinessOutlined,
  DescriptionOutlined,
  FindInPageOutlined,
  FormatAlignLeft as FormatAlignLeftIcon,
  GroupOutlined,
  PeopleAltOutlined as PeopleAltOutlinedIcon,
  PermContactCalendarOutlined,
  SettingsOutlined as SettingsIcon,
  WebOutlined as WebOutlinedIcon,
} from '@mui/icons-material';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjectsOutlined';

import {
  AGENCIES_ENDPOINT,
  APPLICATIONS_ENDPOINT,
  AuthContext,
  CALENDAR_ENDPOINT,
  LEVELS_ENDPOINT,
  PANELS_ENDPOINT,
  POSITIONS_ENDPOINT,
  PRE_SCREENING,
  REFERRALS_ENDPOINT,
  RESUME_SCREENING,
  ROLES,
  SETTINGS_ENDPOINT,
  SKILLS_ENDPOINT,
  STREAMS_ENDPOINT,
  TEAM_REQUIREMENTS_END_POINT,
  TEAMS_ENDPOINT,
  TEMPLATES_ENDPOINT,
  TITLES,
  USERS_ENDPOINT,
} from '../../constants';

import './sideBar.css';

const sidebarIconColors = {
  positions: '#f59e0b',
  applications: '#2563eb',
  referrals: '#14b8a6',
  interviews: '#8b5cf6',
  resumeScreening: '#f97316',
  preScreening: '#06b6d4',
  reports: '#6366f1',
  settings: '#8fb8ff',
  teams: '#0f766e',
  panels: '#7c3aed',
  levels: '#ea580c',
  streams: '#0284c7',
  templates: '#16a34a',
  users: '#db2777',
  skills: '#64748b',
  agencies: '#9333ea',
};

const SideBarLink = React.memo(function SideBarLink({
  to,
  title,
  Icon,
  isActive,
  iconColor,
  onClick,
}) {
  return (
    <div className="side-bar-active-area">
      <Link to={to} className="side-bar-link-button" onClick={onClick}>
        <div className={`side-bar-button ${isActive ? '-active' : ''}`}>
          <div className="side-bar-icon">
            <Icon className="side-bar-icon__size" sx={{ color: iconColor }} />
          </div>
          <div className="side-bar-text">{title}</div>
        </div>
      </Link>
    </div>
  );
});

export function SideBar({ handleDrawerClose }) {
  const location = useLocation();
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles?.[0];
  const shouldShowSettings = ![ROLES.USER, ROLES.CANDIDATE, ROLES.AGENCY].includes(role);

  const [open, setOpen] = useState(false);
  const settingsMenuRef = useRef(null);

  const toggleSettings = () => setOpen((prev) => !prev);
  const closeSettingsMenu = () => {
    setOpen(false);
    handleDrawerClose();
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const isSettingsActive =
    isActive(TEAMS_ENDPOINT) ||
    isActive(TEAM_REQUIREMENTS_END_POINT) ||
    isActive(PANELS_ENDPOINT) ||
    isActive(LEVELS_ENDPOINT) ||
    isActive(STREAMS_ENDPOINT) ||
    isActive(TEMPLATES_ENDPOINT) ||
    isActive(USERS_ENDPOINT) ||
    isActive(SKILLS_ENDPOINT) ||
    isActive(AGENCIES_ENDPOINT) ||
    isActive(SETTINGS_ENDPOINT);

  useEffect(() => {
    if (!isSettingsActive) {
      setOpen(false);
    }
  }, [isSettingsActive]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  useEffect(() => {
    document.body.classList.toggle('side-bar-settings-menu-open', open);

    return () => document.body.classList.remove('side-bar-settings-menu-open');
  }, [open]);

  return (
    <div className="side-bar-container">
      {/* Main Links */}
      <div className="side-bar-with-button">
        <div className="side-bar-buttons">
          {role !== ROLES.USER && (
            <SideBarLink
              to={POSITIONS_ENDPOINT}
              title={TITLES.POSITIONS}
              Icon={EmojiObjectsIcon}
              isActive={isActive(POSITIONS_ENDPOINT)}
              iconColor={sidebarIconColors.positions}
              onClick={closeSettingsMenu}
            />
          )}

          {role !== ROLES.AGENCY && role !== ROLES.USER && (
            <SideBarLink
              to={APPLICATIONS_ENDPOINT}
              title={TITLES.APPLICATIONS}
              Icon={DescriptionOutlined}
              isActive={isActive(APPLICATIONS_ENDPOINT)}
              iconColor={sidebarIconColors.applications}
              onClick={closeSettingsMenu}
            />
          )}

          <SideBarLink
            to={REFERRALS_ENDPOINT}
            title={TITLES.REFERRALS}
            Icon={DescriptionOutlined}
            isActive={isActive(REFERRALS_ENDPOINT, true)}
            iconColor={sidebarIconColors.referrals}
            onClick={closeSettingsMenu}
          />

          {role !== ROLES.AGENCY && (
            <SideBarLink
              to={CALENDAR_ENDPOINT}
              title={TITLES.CALENDAR}
              Icon={PermContactCalendarOutlined}
              isActive={isActive(CALENDAR_ENDPOINT, true)}
              iconColor={sidebarIconColors.interviews}
              onClick={closeSettingsMenu}
            />
          )}

          {(role === ROLES.ADMIN ||
            role === ROLES.SUPER_ADMIN ||
            role === ROLES.TENANT_OWNER ||
            role === ROLES.HIRING_MANAGER) && (
            <SideBarLink
              to={RESUME_SCREENING}
              title={TITLES.RESUME_SCREENING}
              Icon={FindInPageOutlined}
              isActive={isActive(RESUME_SCREENING, true)}
              iconColor={sidebarIconColors.resumeScreening}
              onClick={closeSettingsMenu}
            />
          )}

          {(role === ROLES.ADMIN ||
            role === ROLES.TENANT_OWNER ||
            role === ROLES.SUPER_ADMIN ||
            role === ROLES.HR) && (
            <SideBarLink
              to={PRE_SCREENING}
              title={TITLES.PRE_SCREENING}
              Icon={FindInPageOutlined}
              isActive={isActive(PRE_SCREENING, true)}
              iconColor={sidebarIconColors.preScreening}
              onClick={closeSettingsMenu}
            />
          )}
        </div>
      </div>

      {/* Settings Dropdown */}
      {shouldShowSettings && (
        <div className="side-bar-buttons-items" ref={settingsMenuRef}>
          <div className="side-bar-active-area">
            <div
              className={`setting-title-container ${open || isSettingsActive ? '-active' : ''}`}
              onClick={toggleSettings}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleSettings()}
              data-testid="testSettings"
              aria-expanded={open}
            >
              <div className="setting-title">
                <SettingsIcon
                  className="side-bar-icon__size"
                  sx={{ color: sidebarIconColors.settings }}
                />
                <div className="settings-side-bar-text">{TITLES.SETTINGS}</div>
              </div>
            </div>
          </div>

          {open && (
            <>
              <div
                className="side-bar-settings-backdrop"
                aria-hidden="true"
                onClick={() => setOpen(false)}
              />
              <div className="side-bar-settings-flyout" aria-label="Settings menu">
                <SideBarLink
                  to={TEAMS_ENDPOINT}
                  title={TITLES.TEAMS}
                  Icon={GroupOutlined}
                  iconColor={sidebarIconColors.teams}
                  onClick={closeSettingsMenu}
                  isActive={
                    location.pathname === TEAMS_ENDPOINT ||
                    location.pathname === TEAM_REQUIREMENTS_END_POINT
                  }
                />

                <SideBarLink
                  to={PANELS_ENDPOINT}
                  title={TITLES.PANELS}
                  Icon={GroupOutlined}
                  isActive={isActive(PANELS_ENDPOINT, true)}
                  iconColor={sidebarIconColors.panels}
                  onClick={closeSettingsMenu}
                />
                <SideBarLink
                  to={LEVELS_ENDPOINT}
                  title={TITLES.LEVELS}
                  Icon={BarChartOutlinedIcon}
                  isActive={isActive(LEVELS_ENDPOINT, true)}
                  iconColor={sidebarIconColors.levels}
                  onClick={closeSettingsMenu}
                />
                <SideBarLink
                  to={STREAMS_ENDPOINT}
                  title={TITLES.STREAMS}
                  Icon={WebOutlinedIcon}
                  isActive={isActive(STREAMS_ENDPOINT, true)}
                  iconColor={sidebarIconColors.streams}
                  onClick={closeSettingsMenu}
                />
                <SideBarLink
                  to={TEMPLATES_ENDPOINT}
                  title={TITLES.TEMPLATES}
                  Icon={FormatAlignLeftIcon}
                  isActive={isActive(TEMPLATES_ENDPOINT, true)}
                  iconColor={sidebarIconColors.templates}
                  onClick={closeSettingsMenu}
                />

                {(role === ROLES.ADMIN ||
                  role === ROLES.TENANT_OWNER ||
                  role === ROLES.SUPER_ADMIN) && (
                  <>
                    <SideBarLink
                      to={USERS_ENDPOINT}
                      title={TITLES.USERS}
                      Icon={PeopleAltOutlinedIcon}
                      isActive={isActive(USERS_ENDPOINT, true)}
                      iconColor={sidebarIconColors.users}
                      onClick={closeSettingsMenu}
                    />
                    <SideBarLink
                      to={SKILLS_ENDPOINT}
                      title={TITLES.SKILLS}
                      Icon={SettingsIcon}
                      isActive={isActive(SKILLS_ENDPOINT, true)}
                      iconColor={sidebarIconColors.skills}
                      onClick={closeSettingsMenu}
                    />
                    <SideBarLink
                      to={AGENCIES_ENDPOINT}
                      title={TITLES.AGENCIES}
                      Icon={BusinessOutlined}
                      isActive={isActive(AGENCIES_ENDPOINT, true)}
                      iconColor={sidebarIconColors.agencies}
                      onClick={closeSettingsMenu}
                    />
                  </>
                )}

                {(role === ROLES.ADMIN ||
                  role === ROLES.TENANT_OWNER ||
                  role === ROLES.HR ||
                  role === ROLES.SUPER_ADMIN) && (
                  <SideBarLink
                    to={SETTINGS_ENDPOINT}
                    title={TITLES.GENERAL_SETTINGS}
                    Icon={SettingsIcon}
                    isActive={isActive(SETTINGS_ENDPOINT, true)}
                    iconColor={sidebarIconColors.settings}
                    onClick={closeSettingsMenu}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SideBar;
